import { Box, Grid, Typography, IconButton, TextField, FormControlLabel, Checkbox, Button, Alert } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import { useEffect, useState } from "react"
import { useAppData } from "../../redux/appDataProvider"
import ApiClient from "../../utilities/apiClient"
import PdfPreview from "./PdfPreview"
import { useSelector } from "react-redux"
import AppConfig from "../../AppConfig"
import MyLodder from "../../components/lodder";

const DEFAULT_ALLOCATIONS = {
    1: [100],
    2: [50, 50],
    3: [40, 30, 30],
}

const NomineeDistribution = ({ nominees, nomineesData, guardianData, isUnderage }) => {
    const { userInfo } = useAppData()

    const [allocations, setAllocations] = useState([])
    const [isAcknowledged, setIsAcknowledged] = useState(false);

    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [responseData, setResponseData] = useState({})
    const [pdfError, setPdfError] = useState(null)

    const apiToken = useSelector(state => state.auth.apiToken);

    useEffect(() => {
        const count = nominees.length
        const defaultAllocs = DEFAULT_ALLOCATIONS[count] || []
        setAllocations(defaultAllocs)
    }, [nominees])


    const handleAdjust = (index, change) => {
        const updated = [...allocations];
        let newValue = updated[index] + change;

        if (newValue < 0 || newValue > 100) return;

        // Adjust other fields proportionally to keep total = 100
        const diff = newValue - updated[index];
        const others = allocations.map((_, i) => i).filter(i => i !== index);
        const perOther = diff / others.length;

        let newAlloc = updated.map((val, i) =>
            i === index ? newValue : Math.max(0, val - perOther)
        );

        const total = newAlloc.reduce((a, b) => a + b, 0);

        // Normalize rounding issues
        const correction = 100 - total;
        newAlloc[0] += correction;

        setAllocations([...newAlloc]);
    }

    const total = allocations.reduce((a, b) => a + b, 0).toFixed(2);

    const handleSubmit = async () => {
        const nomineesPayload = nomineesData.map((nominee, index) => {
            if (!nominee) return null;

            const guardian = isUnderage[index] ? guardianData[index] : null;

            return {
                full_name: nominee.nomineeName,
                relationship: nominee.nomineeRelationship,
                dob: nominee.nomineeDob,
                mobile_number: nominee.nomineeMobile,
                email: nominee.nomineeEmail.toLowerCase(),
                proof_type: nominee.nomineeProofType,
                proof_number: nominee.nomineeProofNumber,
                address: nominee.nomineeAddress,
                pincode: nominee.nomineePincode,
                city: nominee.nomineeCity,
                state: nominee.nomineeState,
                country: nominee.nomineeCountry,
                is_minor: isUnderage[index] || false,
                percentage: allocations[index] || 0,
                guardian: guardian
                    ? {
                        full_name: guardian.guardianName,
                        relationship: guardian.guardianRelationship,
                        dob: guardian.guardianDob,
                        mobile_number: guardian.guardianMobile,
                        email: guardian.guardianEmail.toLowerCase(),
                        proof_type: guardian.guardianProofType,
                        proof_number: guardian.guardianProofNumber,
                        address: guardian.guardianAddress,
                        pincode: guardian.guardianPincode,
                        city: guardian.guardianCity,
                        state: guardian.guardianState,
                        country: guardian.guardianCountry
                    }
                    : null
            };
        });

        // User object 
        const user = {
            client_id: userInfo.CLIENT_ID,
            full_name: userInfo.CLIENT_NAME,
            email: userInfo.CLIENT_ID_MAIL,
            mobile_number: userInfo.MOBILE_NO,
            client_dp_id:userInfo.DP_ID
        };

        // Filter out nulls (in case nominee 2 or 3 is not filled)
        const payload = {
            data: {
            user,
            nominees: nomineesPayload.filter(n => n !== null),
            email: userInfo.CLIENT_ID_MAIL,
            mobile: userInfo.MOBILE_NO,
           "source": "nominee",
            }
        };

        setResponseData(payload)

        try {
            setLoading(true);
            setPdfError(null)

            const apiClient = new ApiClient(apiToken);

            apiClient
                .fetchData(
                    AppConfig.api.Request.pdfPreview,
                    "POST",
                    payload
                )
                .then((response) => {
                    if (!response) {
                        console.error("Error: Response is missing.");
                        return;
                    }

                    if (response.status === 200) {
                        // Convert Base64 string to binary data
                        const binaryString = window.atob(response.data);
                        const binaryLen = binaryString.length;
                        const bytes = new Uint8Array(binaryLen);

                        for (let i = 0; i < binaryLen; i++) {
                            bytes[i] = binaryString.charCodeAt(i);
                        }

                        // Create a Blob and generate a URL for preview
                        const blob = new Blob([bytes], { type: "application/pdf" });
                        const pdfUrl = URL.createObjectURL(blob);
                        setPdfUrl(pdfUrl);
                    } else if (response.status === 500) {
                        const errorMessage =
                            response.statusText ||
                            (response.details && response.details["Error Description"]);
                        console.error("Error 500: ", errorMessage);
                        setPdfError(true)
                    } else {
                        console.warn("Unexpected response status:", response.status);
                        setPdfError(true)

                    }
                })
                .catch((error) => {
                    console.error("Error during API request:", error);
                    setPdfError(true)

                })
                .finally(() => {
                    setLoading(false);
                });

        } catch (error) {
            console.error("PDF generation error (outer try/catch):", error);
            setLoading(false);
        }

    };


    return (
        <Box>
            {pdfUrl ? (
                <PdfPreview pdfUrl={pdfUrl} loading={loading} responseData={responseData} />
            ) : pdfError ?
                (<Alert severity="error" sx={{ mt: 2 }}>
                    PDF could not be loaded. Please try again later.
                </Alert>) : (
                    <Grid container direction="column" alignItems="center">
                        <Typography textAlign="center" sx={{ color: "#131E2E", fontSize: { xs: "16px", sm: "18px" }, fontWeight: "bold", mb: 2 }}>
                            Distribution of Nomination Percentage
                        </Typography>
                        <Box
                            sx={{
                                border: "1px solid #817E7E",
                                borderRadius: "8px",
                                p: 2,
                                mb: 2,
                                width: "100%",
                                maxWidth: "500px",
                                mx: "auto"
                            }}
                        >

                            {nominees.map((nominee, ind) => (
                                <Grid
                                    container
                                    key={ind}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mb: 2 }}
                                >
                                    <Box>
                                        <Typography sx={{ color: "#333", fontSize: "14px", fontWeight: 600 }}>
                                            Nominee {ind + 1}
                                        </Typography>

                                        <Typography sx={{ color: "#333", fontSize: "14px", fontWeight: 600 }}>
                                            {nominee?.nomineeName || "-"}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center">
                                        <IconButton sx={{ color: "#333", fontSize: "14px" }} size="small" onClick={() => handleAdjust(ind, -1)}>
                                            <RemoveIcon />
                                        </IconButton>
                                        <TextField
                                            size="small"
                                            value={allocations[ind]?.toFixed(2) || 0}
                                            InputProps={{
                                                readOnly: true,
                                                sx: { width: 70, textAlign: "center", border: "1px solid #817E7E", fontSize: "12px", color: "#333", fontWeight: 600 }
                                            }}
                                        />
                                        <IconButton sx={{ color: "#333", fontSize: "14px" }} size="small" onClick={() => handleAdjust(ind, 1)}>
                                            <AddIcon />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            ))}

                            <Box
                                sx={{
                                    borderTop: "1px solid #999",
                                    pt: 1,
                                    mt: 5,
                                    display: "flex",
                                    justifyContent: "space-between"
                                }}
                            >
                                <Typography sx={{ color: "#6A1B4D", fontSize: { xs: "16px" }, fontWeight: "bold" }}>Total Allocation</Typography>
                                <Typography sx={{ color: "#6A1B4D", fontSize: { xs: "16px" }, fontWeight: "bold", }}>{total}%</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ maxWidth: "600px", width: "100%", mx: "auto", px: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAcknowledged}
                                        onChange={(e) => setIsAcknowledged(e.target.checked)}
                                        sx={{ color: "#333" }}
                                    />
                                }

                                label={
                                    <Typography sx={{ fontSize: "14px", color: "#333" }}>
                                        By proceeding, I acknowledge that I have entered the nomination for my TradeBoss trading and demat account myself and all information added here is true to my knowledge.
                                    </Typography>
                                }
                            />

                            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                                <Button
                                    disabled={!isAcknowledged}
                                    sx={{ color: !isAcknowledged && "#999 !important", fontWeight: 600 }}
                                    onClick={handleSubmit}
                                >
                                    Proceed to Next
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                )}
        <MyLodder canShow={loading} />
        </Box>
    )
}

export default NomineeDistribution
